import { useCallback, useEffect, useState } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  OptionProps,
  MultiValueProps,
  MultiValueRemoveProps,
  MenuProps,
  InputActionMeta,
} from "react-select";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";

interface PolicyValue {
  id: number;
  Policy_id: string;
  Policy_name: string;
  plans?: any[];
  is_active?: boolean | null;
  status?: string;
  Status?: string;
  __inactive?: boolean;
}

interface PolicyOption {
  value: PolicyValue;
  label: string;
  __inactive?: boolean;
}

interface PolicyNamesProps {
  // Kept flexible because this shared component is used by older pages
  // with different handler signatures.
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any[];
  qaIdPrefix?: string;
  isMulti?: boolean;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "policy-names";
// No shared cache: a policy changed on its source page must refresh on remount.
const flag = (v: any): boolean | null => {
  if (v === true || v === 1 || v === "1" || String(v).trim().toLowerCase() === "true") return true;
  if (v === false || v === 0 || v === "0" || String(v).trim().toLowerCase() === "false") return false;
  return null;
};

const policyStatus = (item: any): boolean | null => {
  if (!item || typeof item !== "object") return null;
  const record = item.value && typeof item.value === "object" ? item.value : item;
  const deleted = [record.is_deleted, record.isDeleted, record.is_soft_deleted, record.isSoftDeleted]
    .some(v => flag(v) === true);
  if (deleted) return false;
  const direct = flag(record.is_active ?? record.isActive ?? record.active);
  const word = String(record.status ?? record.Status ?? "").toLowerCase().trim();
  if (direct === false || word === "inactive" || word === "disabled") return false;
  if (direct === true || word === "active" || word === "enabled") return true;
  return null;
};

const isLocked = (option: PolicyOption) =>
  option.__inactive === true || policyStatus(option.value) === false;

const policyKey = (option: PolicyOption): string => {
  const id = Number(option.value?.id);
  return id > 0 ? `id:${id}` : `code:${String(option.value?.Policy_id ?? "").toLowerCase()}`;
};

const mergePolicies = (incoming: PolicyOption[], catalogue: PolicyOption[], previous: PolicyOption[] = []) => {
  const lookup = new Map(catalogue.map(o => [policyKey(o), o]));
  const old = new Map(previous.map(o => [policyKey(o), o]));
  const seen = new Set<string>();
  return incoming.map(item => {
    const key = policyKey(item);
    const canonical = lookup.get(key);
    const prior = old.get(key);
    const status = canonical ? policyStatus(canonical.value) :
      (policyStatus(item.value) ?? (prior ? policyStatus(prior.value) : null));
    const base = canonical ?? item;
    return {
      ...base,
      label: item.label || base.label,
      value: { ...base.value, ...item.value, is_active: status, __inactive: status === false },
      __inactive: status === false,
    } as PolicyOption;
  }).filter(o => {
    const key = policyKey(o);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const normalizePlans = (item: any): any[] => {
  if (Array.isArray(item?.plan)) return item.plan;
  if (Array.isArray(item?.Plan)) return item.Plan;
  if (Array.isArray(item?.plans)) return item.plans;
  return [];
};

const formatPolicyOption = (item: any): PolicyOption => {
  const planArr = normalizePlans(item);
  const policyId = String(item?.policy_id ?? item?.Policy_id ?? "");
  const policyName = String(item?.policy_name ?? item?.Policy_name ?? item?.name ?? "");
  const dbId = Number(item?.id ?? 0);

  const planNames = planArr
    .map((p: any) =>
      String(p?.Plan_name ?? p?.plan_name ?? p?.name ?? "").trim()
    )
    .filter(Boolean)
    .join(", ");

  const planSuffix = planNames ? ` (${planNames})` : "";

  return {
    value: {
      id: Number.isFinite(dbId) ? dbId : 0,
      Policy_id: policyId,
      Policy_name: policyName,
      plans: planArr,
      is_active: policyStatus(item),
      status: item?.status ?? item?.Status,
    },
    label: `(${policyId}) ${policyName}${planSuffix}`,
    __inactive: policyStatus(item) === false,
  };
};

const normalizeDefaultPolicy = (item: any): PolicyOption => {
  // Already in the new PolicyOption format.
  if (item?.value && typeof item.value === "object" && item.value.Policy_id) {
    const plans = Array.isArray(item.value.plans)
      ? item.value.plans
      : Array.isArray(item.value.plan)
        ? item.value.plan
        : [];

    const planNames = plans
      .map((p: any) => String(p?.Plan_name ?? p?.plan_name ?? p?.name ?? ""))
      .filter(Boolean)
      .join(", ");

    return {
      value: {
        id: Number(item.value.id ?? 0),
        Policy_id: String(item.value.Policy_id ?? item.value.policy_id ?? ""),
        Policy_name: String(item.value.Policy_name ?? item.value.policy_name ?? ""),
        plans,
        is_active: policyStatus(item?.value) ?? policyStatus(item),
        status: item?.value?.status ?? item?.value?.Status ?? item?.status ?? item?.Status,
      },
      label:
        item.label ||
        `(${item.value.Policy_id ?? item.value.policy_id ?? ""}) ${
          item.value.Policy_name ?? item.value.policy_name ?? ""
        }${planNames ? ` (${planNames})` : ""}`,
    };
  }

  // Direct policy object from backend / legacy pages.
  if (item?.Policy_id || item?.policy_id) {
    const plans = normalizePlans(item);
    const policyId = String(item.Policy_id ?? item.policy_id ?? "");
    const policyName = String(item.Policy_name ?? item.policy_name ?? item.name ?? "");
    const id = Number(item.id ?? 0);

    const planNames = plans
      .map((p: any) => String(p?.Plan_name ?? p?.plan_name ?? p?.name ?? ""))
      .filter(Boolean)
      .join(", ");

    return {
      value: {
        id: Number.isFinite(id) ? id : 0,
        Policy_id: policyId,
        Policy_name: policyName,
        plans,
        is_active: policyStatus(item),
        status: item?.status ?? item?.Status,
      },
      label: `(${policyId}) ${policyName}${planNames ? ` (${planNames})` : ""}`,
    };
  }

  // Legacy Option format: { value: "POLICY_ID", label: "Policy Name" }
  const policyId = String(item?.value ?? "");
  const policyName = String(item?.label ?? "");

  return {
    value: {
      id: Number(item?.id ?? 0) || 0,
      Policy_id: policyId,
      Policy_name: policyName,
      plans: [],
    },
    label: policyName || policyId,
  };
};

const fetchAllPolicyOptions = async (): Promise<PolicyOption[]> => {
  // Request all statuses, then filter on the client so previously linked
  // inactive policies can still be identified by DB ID on Edit.
  const response = await PolicyAxiosInstance.get("getPolicy");
  const raw = response.data?.result ?? response.data?.policies ??
    response.data?.rows ?? response.data?.data ?? response.data ?? [];
  const data = Array.isArray(raw) ? raw : Array.isArray(raw?.policies) ? raw.policies :
    Array.isArray(raw?.rows) ? raw.rows : Array.isArray(raw?.data) ? raw.data : [];
  return data.map(formatPolicyOption).filter((o: PolicyOption) =>
    Boolean(o.value.Policy_id || o.value.Policy_name));
};

const PolicyNames = ({
  onChange,
  clear,
  defaultValue,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: PolicyNamesProps) => {
  const [options, setOptions] = useState<PolicyOption[]>([]);
  const [allOptions, setAllOptions] = useState<PolicyOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<PolicyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadAllOptions = useCallback(async () => {
    setLoading(true);
    try {
      const formattedOptions = await fetchAllPolicyOptions();
      setAllOptions(formattedOptions);
      setOptions(formattedOptions.filter(o => policyStatus(o.value) === true));
    } catch (error) {
      console.error("Error loading policy options:", error);
      setAllOptions([]);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAllOptions();
  }, [loadAllOptions]);

  useEffect(() => {
    const query = inputValue.trim().toLowerCase();
    const available = allOptions.filter(o => policyStatus(o.value) === true);

    if (!query) {
      setOptions(available);
      return;
    }

    setOptions(
      available.filter((option) => {
        const policyName = String(option.value.Policy_name ?? "").toLowerCase();
        const policyId = String(option.value.Policy_id ?? "").toLowerCase();
        const planNames = (option.value.plans || [])
          .map((plan: any) =>
            String(plan?.Plan_name ?? plan?.plan_name ?? plan?.name ?? "").toLowerCase()
          )
          .join(" ");

        return (
          policyName.includes(query) ||
          policyId.includes(query) ||
          planNames.includes(query)
        );
      })
    );
  }, [inputValue, allOptions]);

  const handleChange = (selected: MultiValue<PolicyOption>) => {
    const proposed = [...selected];
    const kept = selectedOptions.filter(old => isLocked(old) &&
      !proposed.some(option => policyKey(option) === policyKey(old)));
    const updated = mergePolicies([...proposed, ...kept], allOptions, selectedOptions);
    setSelectedOptions(updated);
    // Preserve existing behavior: parent receives PolicyValue objects.
    onChange(updated.map(option => option.value));
  };

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
      setInputValue("");
      setOptions(allOptions.filter(o => policyStatus(o.value) === true));
    }
  }, [clear, allOptions]);

  useEffect(() => {
    if (!defaultValue) return;

    const formattedDefault = Array.isArray(defaultValue)
      ? defaultValue.map(normalizeDefaultPolicy)
      : [];

    setSelectedOptions(previous => mergePolicies(formattedDefault, allOptions, previous));
  }, [defaultValue, allOptions]);

  const customStyles: StylesConfig<PolicyOption, true> = {
    multiValue: (styles, props) => ({
      ...styles,
      backgroundColor: isLocked(props.data) ? "#d1d5db" : "#007BFF",
      border: isLocked(props.data) ? "1px solid #94a3b8" : "none",
      color: isLocked(props.data) ? "#334155" : "#FFFFFF",
      borderRadius: "5px",
    }),
    multiValueLabel: (styles, props) => ({
      ...styles,
      color: isLocked(props.data) ? "#334155" : "#FFFFFF",
      fontWeight: isLocked(props.data) ? 600 : 400,
    }),
    multiValueRemove: (styles, props) => ({
      ...styles,
      display: isLocked(props.data) ? "none" : "flex",
      color: "#FFFFFF",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
    option: (styles, { isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
  };

  const Menu = (props: MenuProps<PolicyOption, true>) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const OptionComponent = (props: OptionProps<PolicyOption, true>) => (
    <div
      {...qaAttrs(
        qaIdPrefix,
        `option-${qaSafe(
          props.data.value.Policy_id || props.data.value.id
        )}`
      )}
      data-option-label={props.data.label}
      data-policy-db-id={props.data.value.id}
      data-policy-id={props.data.value.Policy_id}
      data-policy-name={props.data.value.Policy_name}
      data-policy-plans-count={props.data.value.plans?.length || 0}
    >
      <components.Option {...props} />
    </div>
  );

  const MultiValueComponent = (props: MultiValueProps<PolicyOption, true>) => (
    <div
      {...qaAttrs(
        qaIdPrefix,
        `selected-${qaSafe(
          props.data.value.Policy_id || props.data.value.id
        )}`
      )}
      data-selected-label={props.data.label}
      data-policy-db-id={props.data.value.id}
      data-policy-id={props.data.value.Policy_id}
      data-policy-name={props.data.value.Policy_name}
      data-policy-plans-count={props.data.value.plans?.length || 0}
      data-inactive={isLocked(props.data) ? "true" : "false"}
    >
      <components.MultiValue {...props} />
    </div>
  );

  const MultiValueRemove = (props: MultiValueRemoveProps<PolicyOption, true>) =>
    isLocked(props.data) ? null : <components.MultiValueRemove {...props} />;

  const handleInputChange = (
    value: string,
    actionMeta: InputActionMeta
  ): string => {
    // react-select also fires onInputChange on blur/menu-close/set-value.
    // Only actual typing should trigger a backend search.
    if (actionMeta.action === "input-change") {
      setInputValue(value);
    }

    return value;
  };

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        isClearable={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        aria-label="Policy selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={handleInputChange}
        placeholder="Type to search or select from list..."
        noOptionsMessage={() =>
          loading ? "Loading policies..." : "No policies found"
        }
        styles={customStyles}
        className={`w-100 mb-3 ${selectQaId}`}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) =>
          String(option.value.Policy_id || option.value.id)
        }
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
          MultiValueRemove,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>
    </div>
  );
};

export default PolicyNames;
